<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'
import multiMonthPlugin from '@fullcalendar/multimonth'
import ruLocale from '@fullcalendar/core/locales/ru'
import { useDisplay, useTheme } from 'vuetify/framework'
import { useCalendarStore } from '@/modules/calendar/stores/useCalendarStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import AdverseEventsFilterButtons from '@/modules/audits/stores/useAuditCalendarStore/components/filters/AdverseEventsFilterButtons.vue'
import TicketsFilterButtons from '@/modules/audits/stores/useAuditCalendarStore/components/filters/TicketsFilterButtons.vue'
import CreateTaskModal from '@/modules/tasks/components/CreateTaskModal.vue'
import CalendarSidebarDatePicker from '@/modules/calendar/components/CalendarSidebarDatePicker.vue'
import CalendarYearGrid from '@/modules/calendar/components/CalendarYearGrid.vue'
import { getCalendarEntityColor } from '@/modules/calendar/utils/getCalendarEntityColor'
import { getCalendarEventColors } from '@/modules/calendar/utils/getCalendarEventColors'
import { getCalendarEventLabel } from '@/modules/calendar/utils/getCalendarEventLabel'
import { isDateInRange } from '@/modules/calendar/utils/isDateInRange'
import { openCalendarEvent } from '@/modules/calendar/utils/openCalendarEvent'
import { useCalendarEventTooltip } from '@/modules/calendar/composables/useCalendarEventTooltip'

// 👉 Store
const calendarStore = useCalendarStore()
const { xs, mdAndDown } = useDisplay()
const theme = useTheme()
const feedbackStore = useFeedbackStore()
const { isLeftSidebarOpen } = useResponsiveLeftSidebar()

const showCreateTaskModal = ref(false)
const filterMenus = ref<Record<string, boolean>>({})
const tempSelectedFilters = ref<Record<string, any[]>>({})

// Состояние текущего диапазона для повторных запросов при смене фильтров
const currentRange = ref({ start: '', end: '' })
const currentView = ref('dayGridMonth')

// Мини-календарь сайдбара: выбранная дата синхронизируется с FullCalendar
const refCalendar = ref<InstanceType<typeof FullCalendar> | null>(null)
const sidebarDate = ref<Date>(new Date())

const { hoveredEvent, onEventMouseEnter, onEventMouseLeave } = useCalendarEventTooltip()

const calendarViewOptions = [
  { value: 'multiMonthYear', label: 'Год' },
  { value: 'dayGridMonth', label: 'Месяц' },
  { value: 'dayGridWeek', label: 'Неделя' },
  { value: 'listDay', label: 'День' },
  { value: 'listMonth', label: 'Список' },
]

const changeCalendarView = (view: string) => {
  refCalendar.value?.getApi().changeView(view)
}

watch(sidebarDate, date => {
  refCalendar.value?.getApi().gotoDate(date)
})

const isDarkTheme = computed(() => theme.global.current.value.dark)
const resolveEventColor = (calendarLabel?: string) =>
  getCalendarEntityColor(calendarLabel, isDarkTheme.value)

// Режим «Год» рисуется отдельным компонентом, сетка FullCalendar в нём скрыта
const isYearView = computed(() => currentView.value === 'multiMonthYear')
const currentYear = computed(() => Number(currentRange.value.start.slice(0, 4)))

const yearGridEvents = computed(() => calendarStore.events.map(event => ({
  id: event.id?.toString() || event.title,
  label: getCalendarEventLabel(event),
  title: event.title || 'Без названия',
  start: event.start,
  url: event.url,
  source: event.extendedProps?.source,
  color: resolveEventColor(event.extendedProps?.calendar),
})))

// 👉 Инициализация данных
onMounted(async () => {
  await calendarStore.fetchCalendars()
})

// 👉 Опции для FullCalendar
const calendarOptions = computed(() => {
  return {
    plugins: [dayGridPlugin, interactionPlugin, listPlugin, multiMonthPlugin],
    locale: ruLocale,
    initialView: 'dayGridMonth',
    /*
    * Бургер в тулбаре — кастомная кнопка FullCalendar. Без click drawer
    * с мини-календарём и «Создать задачу» на мобиле не открывается.
    */
    customButtons: {
      drawerToggler: {
        text: '',
        click: () => {
          isLeftSidebarOpen.value = true
        },
      },
    },
    headerToolbar: {
      start: 'drawerToggler,prev,next title',
      end: 'multiMonthYear,dayGridMonth,dayGridWeek,listDay,listMonth',
    },
    views: {
      multiMonthYear: { buttonText: 'Год' },
      /*
      * Число, не true: при contentHeight auto true не срабатывает,
      * и чипы на узкой ячейке наезжают друг на друга.
      */
      dayGridMonth: { buttonText: 'Месяц', dayMaxEvents: 3 },
      dayGridWeek: { buttonText: 'Неделя' },
      listDay: { buttonText: 'День' },
      listMonth: { buttonText: 'Список' },
    },
    /*
    * Подписи кнопок видов заданы в `views`. Здесь оставлен только `today`:
    * верхнеуровневый `buttonText.list` перебивает `views.listDay.buttonText`,
    * из-за чего кнопка «День» превращалась в «Список».
    */
    buttonText: {
      today: 'Сегодня',
    },
    allDayText: 'Весь день',
    moreLinkText: xs.value ? (num: number) => `+${num}` : 'ещё',
    noEventsText: 'Нет событий для отображения',

    /*
    * По умолчанию событие со временем рисуется точкой с текстом, а событие
    * на весь день — блоком. В макете все события одинаковые чипы.
    */
    eventDisplay: 'block',
    displayEventTime: false,

    /*
    * В месяце высоту задают строки (125px из макета), а не пропорция от
    * ширины: иначе на широких экранах ячейки растягиваются вдвое.
    */
    contentHeight: currentView.value === 'dayGridMonth' ? 'auto' : undefined,

    // Срабатывает при инициализации и нажатии кнопок навигации/смены вида
    datesSet: (dateInfo: any) => {
      const viewChanged = currentView.value !== dateInfo.view.type

      currentView.value = dateInfo.view.type

      /*
      * Год стоит на 1 января, и при переключении FullCalendar сохраняет эту
      * дату — иначе месяц/неделя/день открывались бы в январе.
      */
      if (viewChanged && !isDateInRange(new Date(), dateInfo.start, dateInfo.end)) {
        refCalendar.value?.getApi().today()

        return
      }

      /*
      * Навигация тулбаром двигает мини-календарь сайдбара только если
      * сменился месяц, чтобы клик по дню внутри месяца не сбрасывался на 1 число.
      */
      const isSameMonth = sidebarDate.value.getFullYear() === dateInfo.view.currentStart.getFullYear()
        && sidebarDate.value.getMonth() === dateInfo.view.currentStart.getMonth()

      if (!isSameMonth) {
        sidebarDate.value = dateInfo.view.currentStart
      }

      // Обрезаем ISO строку (2026-01-26T00:00:00+07:00) до формата YYYY-MM-DD
      const start = dateInfo.startStr.slice(0, 10)
      const end = dateInfo.endStr.slice(0, 10)

      // Новый объект currentRange пересобирает calendarOptions и перерисовывает календарь
      if (currentRange.value.start === start && currentRange.value.end === end) return

      currentRange.value = { start, end }

      calendarStore.fetchEvents(start, end)
    },

    events: calendarStore.events.map(event => ({
      id: event.id?.toString() || Math.random().toString(),
      title: event.title || 'Без названия',
      start: event.start,
      end: event.end,
      allDay: event.allDay || false,
      ...getCalendarEventColors(resolveEventColor(event.extendedProps?.calendar)),
      extendedProps: {
        ...event.extendedProps,
        url: event.url,
        label: getCalendarEventLabel(event),
        compactLabel: getCalendarEventLabel(event, { compact: true }),
      },
    })),

    /* В сетке событие подписано номером, полное название — в тултипе */
    eventContent: (arg: any) => {
      const node = document.createElement('span')
      const { label, compactLabel } = arg.event.extendedProps

      node.textContent = xs.value ? compactLabel : label

      return { domNodes: [node] }
    },

    eventMouseEnter: onEventMouseEnter,
    eventMouseLeave: onEventMouseLeave,

    eventClick: (info: any) => {
      info.jsEvent?.preventDefault()
      openCalendarEvent({
        id: info.event.id,
        url: info.event.extendedProps?.url,
        extendedProps: info.event.extendedProps,
      })
    },
  }
})

// 👉 Следим за массивом выбранных календарей
watch(
  () => calendarStore.selectedCalendars,
  () => {
    // Передаем сохраненный диапазон, чтобы не "вылететь" из текущего месяца
    calendarStore.fetchEvents(
      currentRange.value.start,
      currentRange.value.end,
    )
  },
  { deep: true },
)

// 👉 Логика фильтрации
const getFilterIcon = (isActive: boolean) =>
  isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

const handleFilterClick = async (filterKey: string) => {
  if (!filterKey) return
  tempSelectedFilters.value[filterKey] = [
    ...((calendarStore.filters as any)[filterKey] || []),
  ]
  await calendarStore.fetchFilterChoices(filterKey)
  filterMenus.value[filterKey] = true
}

const applyFilter = async (filterKey: string) => {
  if (!filterKey) return
  (calendarStore.filters as any)[filterKey] = [
    ...(tempSelectedFilters.value[filterKey] || []),
  ]
  filterMenus.value[filterKey] = false

  // Добавляем параметры диапазона
  await calendarStore.fetchEvents(
    currentRange.value.start,
    currentRange.value.end,
  )
}

// 👉 Check all для сайдбара
const checkAll = computed({
  get: () => calendarStore.selectedCalendars.length === calendarStore.availableCalendars.length,
  set: val => {
    if (val) {
      calendarStore.selectedCalendars = calendarStore.availableCalendars.map(i => i.label)
    } else if (calendarStore.selectedCalendars.length === calendarStore.availableCalendars.length) {
      calendarStore.selectedCalendars = []
    }
    // fetchEvents вызовется автоматически через watcher
  },
})

// 👉 Обработка чипов фильтров
const removeFilterChip = (chip: { label: string; type: string; value?: any }) => {
  calendarStore.removeFilter(chip.type, chip.value)
}

const resetAllFilters = () => {
  calendarStore.resetFilters()
  calendarStore.fetchEvents(
    currentRange.value.start,
    currentRange.value.end,
  )
}

const createEventHandler = () => {
  showCreateTaskModal.value = true
}

const defaultDeadline = computed(() => {
  const date = new Date(sidebarDate.value)
  date.setHours(23, 59, 0, 0)
  if (date.getTime() <= Date.now()) return null
  return date.toISOString()
})

const onTaskCreated = async () => {
  await calendarStore.fetchEvents(currentRange.value.start, currentRange.value.end)
}
</script>

<template>
  <div class="calendar-module d-flex flex-column gap-3">
    <div class="d-flex align-center gap-2 w-100 justify-space-between" :class="{ 'flex-column': xs }">
      <div class="d-flex align-center gap-2">
        <AdverseEventsFilterButtons
          v-if="calendarStore.activeCalendarModule === 'adverse'"
          :store="calendarStore"
          :temp-selected-filters="tempSelectedFilters"
          :filter-menus="filterMenus"
          :get-filter-icon="getFilterIcon"
          @filter-click="handleFilterClick"
          @filter-apply="applyFilter"
        />

        <TicketsFilterButtons
          v-if="calendarStore.activeCalendarModule === 'ticket'"
          :store="calendarStore"
          :temp-selected-filters="tempSelectedFilters"
          :filter-menus="filterMenus"
          :get-filter-icon="getFilterIcon"
          @filter-click="handleFilterClick"
          @filter-apply="applyFilter"
        />
      </div>

      <div class="d-flex align-center gap-2">
        <VBtn
          v-if="calendarStore.activeFilterChips.length"
          prepend-icon="ri-delete-bin-line"
          variant="outlined"
          color="secondary"
          class="text-color-secondary"
          :loading="feedbackStore.isGlobalLoading"
          @click="resetAllFilters"
        >
          Сбросить фильтры
        </VBtn>
      </div>
    </div>

    <div v-if="calendarStore.activeFilterChips.length" class="filter-chip d-flex align-center gap-2 py-3">
      <VChip
        v-for="chip in calendarStore.activeFilterChips"
        :key="`${chip.type}-${chip.value || 'no-value'}`"
        closable
        @click:close="removeFilterChip(chip)"
      >
        {{ chip.label }}
      </VChip>
    </div>

    <VExpansionPanels v-if="xs" class="w-100">
      <VExpansionPanel>
        <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
          <div class="d-flex align-center gap-2">
            <VIcon icon="ri-calendar-2-line" size="20" />
            <span class="text-subtitle-1 font-weight-medium">Вид календаря</span>
          </div>
        </VExpansionPanelTitle>
        <VExpansionPanelText>
          <div class="d-flex flex-column gap-2 w-100">
            <VBtn
              v-for="view in calendarViewOptions"
              :key="view.value"
              class="w-100"
              :active="currentView === view.value"
              variant="outlined"
              color="secondary"
              @click="changeCalendarView(view.value)"
            >
              {{ view.label }}
            </VBtn>
          </div>
        </VExpansionPanelText>
      </VExpansionPanel>
    </VExpansionPanels>

    <VCard>
      <VLayout style="z-index: 0;">
        <VNavigationDrawer
          v-model="isLeftSidebarOpen"
          data-allow-mismatch
          width="344"
          absolute
          touchless
          location="start"
          class="calendar-add-event-drawer"
          :temporary="mdAndDown"
        >
          <div class="d-flex align-center gap-2 pa-5">
            <VBtn
              class="flex-grow-1"
              prepend-icon="ri-add-line"
              @click="createEventHandler"
            >
              Создать задачу
            </VBtn>
            <VBtn
              v-if="xs"
              icon="ri-close-line"
              variant="text"
              aria-label="Закрыть календарь"
              @click="isLeftSidebarOpen = false"
            />
          </div>

          <VDivider />

          <CalendarSidebarDatePicker v-model="sidebarDate" />

          <VDivider />

          <div class="d-flex flex-column pa-5">
            <h5 class="text-h5 mb-4">
              Календари
            </h5>

            <div class="d-flex flex-column calendars-checkbox">
              <VCheckbox
                id="check-all-events"
                v-model="checkAll"
                label="Все календари"
              />
              <VCheckbox
                v-for="(calendar, index) in calendarStore.availableCalendars"
                :id="`${index}`"
                :key="calendar.label"
                v-model="calendarStore.selectedCalendars"
                :value="calendar.label"
                :color="resolveEventColor(calendar.label)"
                :label="calendar.label"
              />
            </div>
          </div>
        </VNavigationDrawer>

        <VMain>
          <VCard flat>
            <!-- Класс на обычном div: VCard не пробрасывает его на корневой элемент -->
            <div :class="{ 'calendar--year-mode': isYearView }">
              <FullCalendar ref="refCalendar" :options="calendarOptions" />

              <VTooltip
                v-if="hoveredEvent"
                :activator="hoveredEvent.el"
                :text="hoveredEvent.title"
                location="top"
                model-value
              />
            </div>

            <CalendarYearGrid
              v-if="isYearView"
              :events="yearGridEvents"
              :year="currentYear"
            />
          </VCard>
        </VMain>
      </VLayout>
    </VCard>
  </div>

  <CreateTaskModal
    v-model="showCreateTaskModal"
    :default-deadline="defaultDeadline"
    @created="onTaskCreated"
  />
</template>

<style lang="scss">
@use "@core/scss/template/libs/full-calendar";
.calendars-checkbox {
  .v-label {
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
    opacity: var(--v-high-emphasis-opacity);
  }
}

.calendar-add-event-drawer {
  &.v-navigation-drawer:not(.v-navigation-drawer--temporary) {
    border-end-start-radius: 0.375rem;
    border-start-start-radius: 0.375rem;
  }

  &.v-navigation-drawer--temporary:not(.v-navigation-drawer--active) {
    transform: translateX(-110%) !important;
  }
}

.filter-chip {
  min-height: 56px;
}

.active-filter {
  border-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-primary)) !important;
}

.calendar-module .v-layout {
  overflow: visible !important;

  .v-card { overflow: visible; }
}

/* В режиме «Год» от FullCalendar нужен только тулбар: сетку заменяет CalendarYearGrid */
.calendar--year-mode .fc-view-harness {
  display: none;
}

@media (max-width: 599px) {
  /* На мобильном виды вынесены в отдельную сворачиваемую панель */
  .calendar-module .fc-button-group:has(.fc-dayGridMonth-button) {
    display: none !important;
  }
}
</style>
